import { useState, useRef, useEffect, useMemo } from 'react'
import { useTimelineSlots } from '../../hooks/useTimelineSlots'
import { useLanguage } from '../../i18n/LanguageContext'

export function DailyTimeline() {
  const slots = useTimelineSlots()
  const { t } = useLanguage()
  const [selectedHour, setSelectedHour] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedSlot = selectedHour !== null ? slots.find((s) => s.hour === selectedHour) : null

  // Compute rate range for bar height normalization and multiplier
  const { minRate, maxRate, multiplier } = useMemo(() => {
    const rates = slots.map((s) => s.rateAgorot)
    const min = Math.min(...rates)
    const max = Math.max(...rates)
    const mult = min > 0 ? (max / min).toFixed(1) : '1.0'
    return { minRate: min, maxRate: max, multiplier: mult }
  }, [slots])

  function getBarHeight(rateAgorot: number): number {
    if (maxRate === minRate) return 100
    // Normalize between 30% and 100%
    return 30 + ((rateAgorot - minRate) / (maxRate - minRate)) * 70
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSelectedHour(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleBarClick(hour: number) {
    setSelectedHour((prev) => (prev === hour ? null : hour))
  }

  const multiplierFn = t('timeline.multiplier') as (x: string) => string

  return (
    <div ref={containerRef} className="glass p-5 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
      <h2 className="font-heading text-lg font-bold text-white mb-4">
        {t('timeline.title') as string}
      </h2>

      <div dir="ltr" className="space-y-2">
        <div className="relative flex items-end gap-[2px] h-16 rounded-lg overflow-visible">
          {slots.map((slot) => {
            const heightPct = getBarHeight(slot.rateAgorot)
            return (
              <button
                key={slot.hour}
                onClick={() => handleBarClick(slot.hour)}
                className={`relative flex-1 transition-all duration-300 first:rounded-s-lg last:rounded-e-lg rounded-t-sm ${
                  slot.tier === 'peak'
                    ? 'bg-red-500/60 hover:bg-red-500/80'
                    : 'bg-emerald-500/40 hover:bg-emerald-500/60'
                } ${slot.isCurrent ? 'ring-2 ring-yellow-400 ring-inset z-10' : ''} ${
                  selectedHour === slot.hour ? 'ring-2 ring-white z-20' : ''
                }`}
                style={{ height: `${heightPct}%` }}
              >
                {slot.isCurrent && (
                  <>
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                      <span className="text-yellow-400 text-xs">▼</span>
                    </div>
                    <div
                      className="absolute inset-0 rounded-t-sm"
                      style={{
                        background: slot.tier === 'peak'
                          ? 'linear-gradient(to top, rgba(250,204,21,0.25), transparent)'
                          : 'linear-gradient(to top, rgba(250,204,21,0.20), transparent)',
                      }}
                    />
                  </>
                )}
              </button>
            )
          })}

          {selectedSlot && selectedHour !== null && (
            <div
              className="absolute -top-16 z-30 bg-slate-800 border border-white/20 rounded-lg px-3 py-1.5 text-xs text-white shadow-lg whitespace-nowrap"
              style={{
                left: `${((selectedHour + 0.5) / 24) * 100}%`,
                transform: 'translateX(-50%)',
              }}
            >
              <span className="font-bold">{selectedHour}:00–{selectedHour + 1}:00</span>
              <span className="mx-1.5 text-slate-500">·</span>
              <span className={selectedSlot.tier === 'peak' ? 'text-red-400' : 'text-emerald-400'}>
                {selectedSlot.tier === 'peak' ? t('timeline.peakShort') : t('timeline.offpeakShort')}
              </span>
              <span className="mx-1.5 text-slate-500">·</span>
              <span>{(selectedSlot.rateAgorot / 100).toFixed(2)} {t('timeline.popoverRate') as string}</span>
            </div>
          )}
        </div>

        <div className="relative h-4 text-[10px] text-slate-500">
          <span className="absolute left-0">0</span>
          <span className="absolute left-1/4 -translate-x-1/2">6</span>
          <span className="absolute left-1/2 -translate-x-1/2">12</span>
          <span className="absolute left-3/4 -translate-x-1/2">18</span>
          <span className="absolute right-0">24</span>
        </div>

        <div className="flex justify-center gap-6 text-xs text-slate-400 mt-2">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/60" />
            {t('timeline.peak') as string}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-500/40" />
            {t('timeline.offpeak') as string}
          </span>
        </div>

        {multiplier !== '1.0' && (
          <p className="text-center text-xs text-slate-500 mt-1">
            {multiplierFn(multiplier)}
          </p>
        )}
      </div>
    </div>
  )
}
